import type { ObjectGlow, ObjectShadow, SceneObject } from '../types';
import { parseObjectGlow, parseObjectShadow } from './objectVisualStyle';

export interface ImageBodyData {
  src?: string;
  shadow?: ObjectShadow;
  glow?: ObjectGlow;
}

function extractSrcFromHtml(body: string): string | undefined {
  const match = body.match(/src\s*=\s*["']([^"']+)["']/i);
  return match?.[1];
}

export function parseImageBody(body: string | undefined | null): ImageBodyData {
  if (!body) return {};
  const trimmed = body.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as ImageBodyData;
      if (parsed && typeof parsed === 'object') {
        return {
          src: typeof parsed.src === 'string' ? parsed.src : undefined,
          shadow: parseObjectShadow(parsed.shadow),
          glow: parseObjectGlow(parsed.glow),
        };
      }
    } catch {
      // fall through to HTML
    }
  }
  return { src: extractSrcFromHtml(body) };
}

export function imageBodyFromSceneObject(
  obj: Pick<SceneObject, 'src' | 'shadow' | 'glow'>
): ImageBodyData {
  const data: ImageBodyData = {};
  if (obj.src) data.src = obj.src;
  if (obj.shadow != null) data.shadow = obj.shadow;
  if (obj.glow != null) data.glow = obj.glow;
  return data;
}

export function serializeImageBody(data: ImageBodyData): string {
  return JSON.stringify(imageBodyFromSceneObject(data as SceneObject));
}

function persistableImageSrc(src?: string): string {
  if (!src) return '';
  if (/^https?:\/\//i.test(src) || src.startsWith('data:')) {
    try {
      const path = new URL(src).pathname;
      const fileName = path.split('/').filter(Boolean).pop();
      return fileName ?? src;
    } catch {
      const fileName = src.split('?')[0]?.split('/').filter(Boolean).pop();
      return fileName ?? src;
    }
  }
  return src;
}

/** Prefer JSON body when effects exist; otherwise keep a simple img tag for legacy readers. */
export function serializeImageBodyForPersist(
  obj: Pick<SceneObject, 'src' | 'shadow' | 'glow'>
): string {
  const src = persistableImageSrc(obj.src);
  const hasEffects = obj.shadow != null || obj.glow != null;

  if (hasEffects) {
    return serializeImageBody({
      src,
      shadow: obj.shadow,
      glow: obj.glow,
    });
  }

  return `<img src="${src}" />`;
}

export function resolveImageFieldsFromBody(
  type: string,
  body: string | undefined
): Partial<Pick<SceneObject, 'src' | 'shadow' | 'glow'>> {
  if (type !== 'image') return {};
  const parsed = parseImageBody(body);
  return {
    src: parsed.src,
    shadow: parsed.shadow,
    glow: parsed.glow,
  };
}

export function extractAssetPathFromBody(body: string | undefined | null): string | undefined {
  if (!body) return undefined;
  const parsed = parseImageBody(body);
  if (parsed.src) return parsed.src;
  const trimmed = body.trim();
  if (/\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(trimmed)) return trimmed;
  return undefined;
}
